// Session navigateur (lot 6.4) : le jeton vit dans un cookie HttpOnly posé par l'API,
// inaccessible au JavaScript. Ce module :
//  - ajoute `credentials: 'include'` et l'en-tête anti-CSRF à TOUS les appels vers l'API ;
//  - retire les en-têtes « Authorization: Bearer null » produits par l'ancien code ;
//  - conserve le jeton historique éventuellement présent (sessions ouvertes avant la migration)
//    jusqu'à la prochaine déconnexion, sans jamais en créer de nouveau.
import { ENV_CONFIG } from '../config/env';

const API_BASE = ENV_CONFIG.API_BASE_URL.replace(/\/+$/, '');
export const CSRF_HEADER = 'X-Hubimmo-CSRF';

function isApiUrl(url) {
  if (!url) return false;
  const s = String(url);
  return s.startsWith(API_BASE) || s.startsWith('/api/');
}

let installed = false;

export function installApiFetch() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (!isApiUrl(url)) return nativeFetch(input, init);
    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
    const auth = headers.get('Authorization');
    if (auth !== null && /^Bearer\s*(null|undefined)?\s*$/i.test(auth.trim())) headers.delete('Authorization');
    if (!headers.has(CSRF_HEADER)) headers.set(CSRF_HEADER, '1');
    trackLoading(1, url);
    return nativeFetch(input, { ...init, headers, credentials: 'include' })
      .finally(() => trackLoading(-1, url));
  };
}

// Chargement global (animations.css) : barre en haut de page dès qu'un appel API est en cours et, si l'attente
// dépasse 0,35 s (liste, import, export…), pastille avec le nom « Hubimmo. » qui s'écrit lettre par lettre.
// Une fois affichée, la pastille reste le temps d'écrire le nom (on le retient), sans texte ni roue.
// Les interrogations silencieuses en arrière-plan (notifications) ne la déclenchent pas.
const SILENT = /\/api\/(notifications(\/unread[^?]*)?|v1\/auth\/(me|email-verification|google\/config)|health)(\?|$)/;
const PILL_MIN_MS = 2200;
let pendingCalls = 0;
let hideTimer = null;
let pillTimer = null;
let pillShownAt = 0;
const LETTERS = [['H', 'hub'], ['u', 'hub'], ['b', 'hub'], ['i', 'immo'], ['m', 'immo'], ['m', 'immo'], ['o', 'immo'], ['.', 'dot']];
function ensureEl(id, build) {
  let el = document.getElementById(id);
  if (!el && document.body) {
    el = document.createElement('div');
    el.id = id;
    el.setAttribute('aria-hidden', 'true');
    if (build) build(el);
    document.body.appendChild(el);
  }
  return el;
}
const buildPill = (el) => {
  const word = document.createElement('div');
  word.className = 'hb-boot-word';
  LETTERS.forEach(([ch, kind], i) => {
    const span = document.createElement('span');
    span.className = `hb-${kind}`;
    span.style.setProperty('--i', String(i));
    span.textContent = ch;
    word.appendChild(span);
  });
  el.appendChild(word);
};
function showPill(pill) {
  if (pill.classList.contains('active')) return;
  // Écran de chargement plein écran déjà affiché : pas de doublon
  if (document.querySelector('.hb-boot:not(.hb-boot--inline)')) return;
  // Rejoue l'écriture du nom à chaque apparition
  const word = pill.firstElementChild;
  if (word) { pill.removeChild(word); void pill.offsetWidth; pill.appendChild(word); }
  pill.classList.add('active');
  pillShownAt = Date.now();
}
function trackLoading(delta, url = '') {
  if (typeof document === 'undefined') return;
  if (SILENT.test(String(url))) return;
  pendingCalls = Math.max(0, pendingCalls + delta);
  const bar = ensureEl('hb-progress');
  const pill = ensureEl('hb-loading-pill', buildPill);
  if (!bar || !pill) return;
  clearTimeout(hideTimer);
  if (pendingCalls > 0) {
    bar.classList.add('active');
    if (!pillTimer && !pill.classList.contains('active')) pillTimer = setTimeout(() => { pillTimer = null; if (pendingCalls > 0) showPill(pill); }, 350);
  } else {
    clearTimeout(pillTimer);
    pillTimer = null;
    const wait = pill.classList.contains('active') ? Math.max(180, PILL_MIN_MS - (Date.now() - pillShownAt)) : 180;
    hideTimer = setTimeout(() => { bar.classList.remove('active'); pill.classList.remove('active'); }, wait);
  }
}

// Jeton historique (avant migration vers le cookie) : lu, jamais écrit.
export function getLegacyToken() {
  try { return localStorage.getItem('token'); } catch { return null; }
}

export function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}

export function clearLocalSession() {
  for (const key of ['token', 'user', 'superadmin_backup_token']) {
    try { localStorage.removeItem(key); } catch { /* stockage indisponible */ }
  }
}

// Déconnexion : révoque la session côté serveur et efface le cookie HttpOnly.
export async function logoutSession() {
  const legacyToken = getLegacyToken();
  clearLocalSession();
  try {
    await fetch(`${API_BASE}/api/v1/auth/logout`, {
      method: 'POST',
      keepalive: true,
      // session historique éventuelle : révoquée elle aussi
      headers: legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {}
    });
  } catch { /* hors ligne : la session locale est déjà effacée */ }
}
